import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, Download, Calculator, ExternalLink, Eye, FileCheck2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/ccmi-cap-bati`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatEUR = (n) => Number(n || 0).toLocaleString('fr-FR', {
  style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2,
});

export const CapBatiResultCard = ({ result, form, issuing, zippingDocs, setZippingDocs, issuedDocs, issueError, handleIssueDocuments, loading, error }) => {
  return (
          <Card className="border-emerald-200" data-testid="cap-bati-result-card">
            <CardHeader className="bg-emerald-50 rounded-t-lg">
              <CardTitle className="text-base flex items-center gap-2">
                <Calculator className="h-5 w-5 text-emerald-600" />
                Devis CAP BATI+ CMI {loading && <span className="text-xs text-slate-400">(calcul...)</span>}
              </CardTitle>
              <CardDescription>
                {result?.reference
                  ? <span className="font-mono text-xs" data-testid="result-reference">{result.reference}</span>
                  : 'Référence générée automatiquement'}
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded mb-4 text-sm" data-testid="error-msg">
                  {error}
                </div>
              )}

              {!result ? (
                <div className="text-center py-10 text-slate-400">
                  Saisissez les paramètres pour voir le résultat.
                </div>
              ) : (
                <div className="space-y-3 text-sm" data-testid="result-content">
                  <Row label="CAP BATI+ CMI" formula="" amount={result.cap_bati_plus_cmi_amount} testid="row-cap-bati" />
                  {result.gfa_activated && (
                    <Row label="Option GFA" formula="" amount={result.gfa_option_amount} testid="row-gfa" />
                  )}
                  <div className="border-t pt-3 font-semibold">
                    <Row label="Montant de base" formula="avant contributions" amount={result.subtotal} testid="row-subtotal" />
                  </div>
                  <Row label="Contribution FOGEDOM/SCIC" formula="" amount={result.fogedom_scic_contribution} testid="row-fogedom" />
                  <Row label="Contribution Expertise préventive" formula="" amount={result.expertise_preventive_contribution} testid="row-expertise" />
                  <Row label="Total contributions" formula="cumul" amount={result.total_contributions} testid="row-total-contrib" muted />

                  {/* V38.20 — Forfait revue de conformité (si activé) */}
                  {result.legal_review_activated && (
                    <Row
                      label="Forfait Revue de conformité CCMI"
                      formula="TTC"
                      amount={result.legal_review_amount_ttc}
                      testid="row-legal-review"
                    />
                  )}

                  <div className="border-t-2 border-emerald-300 pt-3 mt-3">
                    <div className="flex justify-between items-center bg-emerald-50 -mx-4 px-4 py-3 rounded">
                      <span className="font-bold text-emerald-900">MONTANT GLOBAL</span>
                      <span className="text-2xl font-extrabold text-emerald-700 tracking-tight" data-testid="result-total">
                        {formatEUR(result.total)}
                      </span>
                    </div>
                  </div>

                  <details className="mt-4 text-xs">
                    <summary className="cursor-pointer text-slate-500 hover:text-slate-700">Détail des lignes de calcul</summary>
                    <ul className="mt-2 space-y-1 ml-4 text-slate-600" data-testid="result-lines-detail">
                      {result.lines.map((l, i) => (
                        <li key={i} className="border-l-2 border-slate-200 pl-2">
                          <strong>{l.label}</strong> = <span className="font-mono">{formatEUR(l.amount)}</span>
                        </li>
                      ))}
                    </ul>
                  </details>

                  {/* V38.15 — Émission documentaire */}
                  <div className="border-t-2 border-emerald-300 mt-5 pt-5 space-y-3">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <h3 className="font-bold text-slate-900 flex items-center gap-2">
                          <FileCheck2 className="h-5 w-5 text-emerald-700" />
                          Documents officiels (PDF + QR vérifiable)
                        </h3>
                        <p className="text-xs text-slate-500 mt-1">
                          {form.include_gfa
                            ? '6 documents seront générés (3 attestation + 3 GFA)'
                            : '3 documents seront générés (attestation CMI Constructeur)'}
                        </p>
                      </div>
                      <Button
                        onClick={handleIssueDocuments}
                        disabled={issuing}
                        className="bg-emerald-700 hover:bg-emerald-800 text-white whitespace-nowrap"
                        data-testid="issue-documents-btn"
                      >
                        {issuing
                          ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Génération…</>
                          : <><FileCheck2 className="h-4 w-4 mr-2" /> Générer les PDF vérifiables</>}
                      </Button>
                    </div>

                    {issueError && (
                      <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded text-xs" data-testid="issue-error-msg">
                        {issueError}
                      </div>
                    )}

                    {issuedDocs && issuedDocs.issued_documents && issuedDocs.issued_documents.length > 0 && (
                      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 space-y-2" data-testid="issued-docs-list">
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <div className="text-xs text-emerald-900 font-semibold">
                            ✅ {issuedDocs.issued_count} document{issuedDocs.issued_count > 1 ? 's' : ''} émis et inscrit{issuedDocs.issued_count > 1 ? 's' : ''} au registre documentaire
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={zippingDocs}
                            onClick={async () => {
                              setZippingDocs(true);
                              try {
                                const tokens = issuedDocs.issued_documents.map((d) => d.verification_token);
                                const ref = (issuedDocs.simulation_reference || 'cap-bati').replace(/\s+/g, '_');
                                const r = await axios.post(
                                  `${API}/cap-bati-plus-cmi/documents-zip`,
                                  { verification_tokens: tokens, filename_prefix: `documents-${ref}` },
                                  { responseType: 'blob' },
                                );
                                const blob = new Blob([r.data], { type: 'application/zip' });
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `documents-${ref}-${new Date().toISOString().slice(0,10)}.zip`;
                                a.click();
                                setTimeout(() => URL.revokeObjectURL(url), 5000);
                                toast.success('ZIP téléchargé');
                              } catch {
                                toast.error('Échec téléchargement ZIP');
                              } finally {
                                setZippingDocs(false);
                              }
                            }}
                            className="border-emerald-400 text-emerald-800 hover:bg-emerald-100 whitespace-nowrap"
                            data-testid="zip-all-docs-btn"
                          >
                            {zippingDocs
                              ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> ZIP…</>
                              : <><Download className="h-3 w-3 mr-1" /> Tous les PDF (ZIP)</>}
                          </Button>
                        </div>
                        <ul className="space-y-2">
                          {issuedDocs.issued_documents.map((doc) => (
                            <li
                              key={doc.id}
                              className="bg-white border border-emerald-200 rounded p-2 text-xs"
                              data-testid={`issued-doc-${doc.document_step}`}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <div className="font-semibold text-slate-900 truncate">{doc.title}</div>
                                  <div className="text-slate-500 font-mono mt-0.5">
                                    {doc.reference} · {doc.document_type}
                                  </div>
                                  <div className="text-slate-400 text-[10px] font-mono mt-1 break-all">
                                    PDF SHA-256: {doc.pdf_sha256.slice(0, 32)}…
                                  </div>
                                </div>
                                <div className="flex flex-col items-end gap-1 shrink-0">
                                  <a
                                    href={`${BACKEND_URL}/api/public/verify/${doc.verification_token}/pdf`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-700 hover:text-blue-900 underline whitespace-nowrap flex items-center gap-1 text-xs"
                                    data-testid={`view-pdf-${doc.document_step}`}
                                    title="Visualiser le PDF dans un nouvel onglet"
                                  >
                                    <Eye className="h-3 w-3" /> Voir
                                  </a>
                                  <a
                                    href={`${BACKEND_URL}/api/public/verify/${doc.verification_token}/pdf`}
                                    download={`${doc.reference}.pdf`}
                                    className="text-slate-700 hover:text-slate-900 underline whitespace-nowrap flex items-center gap-1 text-xs"
                                    data-testid={`download-pdf-${doc.document_step}`}
                                    title="Télécharger le PDF"
                                  >
                                    <Download className="h-3 w-3" /> Télécharger
                                  </a>
                                  <a
                                    href={doc.verification_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-emerald-700 hover:text-emerald-900 underline whitespace-nowrap flex items-center gap-1 text-xs"
                                    data-testid={`verify-link-${doc.document_step}`}
                                    title="Page de vérification publique (QR)"
                                  >
                                    <ExternalLink className="h-3 w-3" /> Vérifier
                                  </a>
                                </div>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
  );
};

function Row({ label, formula, amount, testid, muted = false }) {
  return (
    <div className={`flex justify-between items-baseline ${muted ? 'text-slate-500' : ''}`} data-testid={testid}>
      <div>
        <div className="font-medium">{label}</div>
        <div className="text-xs text-slate-400 font-mono">{formula}</div>
      </div>
      <div className="font-mono font-semibold whitespace-nowrap ml-3">{formatEUR(amount)}</div>
    </div>
  );
}
